<template>
  <div class="writer">
    <div class="container pt-4">
      <div class="d-flex justify-content-end mb-2">

        <!-- Erase All button -->
        <button class="btn btn-primary btn-sm align-self-center m-2" @click="clearPost()">
          <i class="fad fa-eraser"></i>&nbsp;&nbsp; Erase All
        </button>
      </div>

      <!-- Banner -->
      <Banner @templateChange="handleTemplateChange" />

      <!-- gap -->
      <div class="m-4"></div>

      <!-- Post Form -->
      <PostForm @change="handleFormChange" v-if="post" :_post="post" />
    </div>

    <!-- Text Editor -->
    <div class="container mt-4">
      <Editor
        @change="handleEditorChange"
        @countChange="handleCountChange"
        v-if="post"
        :_post="post"
      />
    </div>

    <!-- Sticky footer -->
    <div class="controls d-flex justify-content-between">

      <!-- Words Count -->
      <div class="postStats d-flex">
        <div class="d-flex align-self-center">

          <!-- Total Words Count -->
          <span> <i class="fad fa-typewriter"></i>&nbsp; {{ wordCount }} words </span>
          <!-- gap -->
          <div class="m-1"></div>

          <!-- Selected Words Count -->
          <span v-if="currentBlockCount">
            <i class="fad fa-tasks-alt"></i>&nbsp; {{ currentBlockCount.chars }} chars,
            {{ currentBlockCount.words }} words selected
          </span>
        </div>
      </div>

      <!-- Buttons -->
      <div class="d-flex">

        <!-- Save Status -->
        <div class="align-self-center">
          <i
            class="fad fa-check-circle"
            :class="{
              'text-muted': saving,
              'text-success': !saving,
              'text-danger': errorsCount > 0,
            }"
          ></i
          >&nbsp;
          <template v-if="saving">Saving...</template>
          <template v-else>Saved</template>
          <span class="--text-sm" v-if="errorsCount > 0"
            >&nbsp;({{ errorsCount }} error{{ errorsCount > 1 ? "s" : "" }})</span
          >
        </div>

        <!-- gap -->
        <div class="m-2"></div>

        <!-- buttons -->
        <div class="buttons d-flex">
          <button class="btn btn-primary btn-sm" @click="exportZip()">
            Generate Zip
          </button>

          <div class="m-1"></div>

          <button class="btn btn-primary btn-sm" @click="preview()">
            Preview
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Editor from "@/components/Editor.vue";
import PostForm from "@/components/PostForm.vue";
import Banner from "@/components/Banner.vue";
import Card from "@/components/Card.vue";
import { toMarkdown } from "@/services/json2md";
import { toHtmlPreview } from "@/services/json2html";
import { updateDB, getDB } from "@/services/db";
import { exportPost } from "@/services/export";
import { countW } from "@/utils";
import { sanitize } from "@/utils";

export default {
  name: "Writer",
  components: { Editor, PostForm, Card, Banner },
  data() {
    return {
      saving: true,
      lastSavedTime: Date.now(),
      wordCount: 0,
      currentBlockCount: null,
      errorsCount: 0,
      post: null,
    };
  },

  async created() {
    await this.fetchPostFromDB();
  },

  methods: {
    async fetchPostFromDB() {
      let p = await getDB();
      if (p) {
        this.post = p;
        this.countWords();
      } else this.initPost();
    },

    async handleTemplateChange(templateName) {
      let resp = (await axios.get(`/samples/${templateName}.json`)).data;
      this.post = null;
      this.post = resp;
      await this.handleEditorChange();
      location.reload();
    },

    initPost() {
      this.post = {
        title: null,
        titleHtml1: null,
        titleHtml2: null,
        categories: null,
        tags: null,
        description: null,
        date: null,
        url: null,
        verticalThumbnail: null,
        horizontalThumbnail: null,
        faqs: [],
        head: null,
        content: null,
        jsonBlocks: null,
      };
    },

    async clearPost() {
      let erase = confirm("Are you sure you want to erase all data ?");

      if (erase) {
        this.initPost();
        await this.handleEditorChange();
        location.reload();
      }
    },

    handleCountChange(countData) {
      this.currentBlockCount = countData;
    },

    handleFormChange(payload) {
      this.errorsCount = payload.errorsCount;
      if (payload.post)
        Object.keys(payload.post).forEach((key) => (this.post[key] = payload.post[key]));
      this.handleEditorChange();
    },

    async handleEditorChange(payload) {
      if (payload && payload.jsonBlocks) {
        this.post.jsonBlocks = payload.jsonBlocks;
        this.post.content = toMarkdown(payload.jsonBlocks);
        this.post.htmlPreview = toHtmlPreview(payload.jsonBlocks);
      }

      this.countWords();
      this.generatePostHead();
      this.saving = true;
      await updateDB(this.post);
      this.lastSavedTime = Date.now();
      console.log("Saved to DB");
      this.saving = false;
      localStorage.lastSavetAt = this.lastSavedTime;
    },

    generatePostHead() {
      try {
        let head = {
          title: sanitize(this.post.title),
          titlehtml:
            sanitize(this.post.titleHtml1) +
            "<br>" +
            `<span class="highlight">${sanitize(this.post.titleHtml2)}</span>`,
          description: sanitize(this.post.description),
          tags: "[" + sanitize(this.post.tags) + "]",
          categories: "[" + sanitize(this.post.categories) + "]",
          image:
            "/assets/img/posts/" +
            sanitize(this.post.url) +
            "." +
            this.post.horizontalThumbnail.name.split(".").pop(),
          wc: this.wordCount,
          faq: this.post.faqs
            .map((f) => {
              return { q: sanitize(f.q), a: sanitize(f.a) };
            })
            .map((f) => `\n- question: ${f.q}\n  answer: ${f.a}`),
        };

        let headStr = "---";
        Object.keys(head).forEach((key) => (headStr += "\n" + key + ": " + head[key]));
        headStr += "\n---";
        this.post.head = headStr;
      } catch (e) {}
    },

    countWords() {
      let introTextCount = countW(this.post.title) + countW(this.post.description);

      let faqCount = 0;
      if (this.post.faqs)
        this.post.faqs.forEach((f) => (faqCount += countW(f.q) + countW(f.a)));

      let contentCount = 0;
      let editor = document.getElementById("editor");
      if (editor) {
        [...editor.querySelectorAll(".ce-block")].forEach(
          (el) => (contentCount += countW(el.innerText))
        );

        [...editor.querySelectorAll(".cdx-input")].forEach(
          (el) => (contentCount += countW(el.value))
        );
      }

      this.wordCount = introTextCount + faqCount + contentCount;
      localStorage.wordCount = this.wordCount;
    },

    preview() {
      window.open(window.location.href + "preview", "_blank").focus();
    },

    exportZip() {
      exportPost(this.post);
    },
  },

  computed: {},
};
</script>

<style scoped lang="scss">
.controls {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  background-color: #ffd199;
  padding: 0.25rem;

  .postStats {
    font-size: 0.8rem;
    display: flex;
  }
}
</style>
